import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as fabric from "fabric";
import { toast } from "sonner";
import {
  ArrowLeft, MousePointer2, Minus, Square, Circle as CircleIcon, Waypoints, ArrowUpRight,
  Pencil, Type, Ruler as RulerIcon, Image as ImageIcon, Save, Trash2, Spline as CurveIcon,
  Move3d, MapPin, Undo2, Redo2, Crosshair, CheckSquare, Eraser, Layers, Move, Lock, Unlock, Gauge, Printer,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { FACE_LABEL } from "@/lib/designs";
import LoadingScreen from "@/components/LoadingScreen";
import NoAccess from "@/pages/NoAccess";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const W = 1400;
const H = 900;

const TOOLS = [
  { key: "select", label: "Seleccionar", icon: MousePointer2 },
  { key: "line", label: "Línea", icon: Minus },
  { key: "arrow", label: "Flecha", icon: ArrowUpRight },
  { key: "curve", label: "Curva", icon: CurveIcon },
  { key: "polygon", label: "Polígono (varios lados)", icon: Waypoints },
  { key: "rect", label: "Rectángulo", icon: Square },
  { key: "ellipse", label: "Círculo", icon: CircleIcon },
  { key: "freehand", label: "Mano alzada", icon: Pencil },
  { key: "text", label: "Texto", icon: Type },
  { key: "ruler", label: "Regla", icon: RulerIcon },
];

// ---- edición de vértices de polígonos (estilo SketchUp) ----
// Fabric v7 ships `createPolyControls` which correctly handles vertex
// position/action using the new API (sendPointToPlane, calcOwnMatrix,
// setDimensions, dirty). Using it avoids drift between fabric internals.
function enablePolygonEditing(poly, canvas) {
  if (!poly || poly.type !== "polygon") return false;
  poly.edit = true;
  poly.objectCaching = false;
  poly.cornerStyle = "circle";
  poly.cornerColor = "#C75B28";
  poly.cornerSize = 10;
  poly.transparentCorners = false;
  poly.hasBorders = false;
  poly.controls = fabric.controlsUtils.createPolyControls(poly);
  // setCoords() MUST be called AFTER replacing `controls` so that
  // fabric populates `oCoords` with entries for every control key
  // (p0..pN). Otherwise drawControls → oCoords[key] is undefined and
  // throws "Cannot read properties of undefined (reading 'x')".
  poly.setCoords();
  canvas.setActiveObject(poly);
  canvas.requestRenderAll();
  return true;
}
function disablePolygonEditing(poly, canvas) {
  if (!poly) return;
  poly.edit = false;
  poly.objectCaching = true;
  poly.hasBorders = true;
  poly.controls = fabric.controlsUtils.createObjectDefaultControls();
  poly.setCoords();
  canvas.requestRenderAll();
}

export default function DrawingEditor() {
  const { projectId, designId, drawingId } = useParams();
  const navigate = useNavigate();
  const canvasElRef = useRef(null);
  const canvasAreaRef = useRef(null);
  const fcRef = useRef(null);
  const toolRef = useRef("select");
  const styleRef = useRef({ stroke: "#1E3A5F", fill: "rgba(199,91,40,0.15)", width: 2 });
  const tempRef = useRef(null);
  const bgPathRef = useRef(null);
  const bgImgRef = useRef(null);
  const historyRef = useRef({ stack: [], index: -1, locked: false });
  const autosaveTimer = useRef(null);
  const persistRef = useRef(null);
  const savingRef = useRef(false);
  const bgEditModeRef = useRef(false);

  const [drawing, setDrawing] = useState(null);
  const [projectInfo, setProjectInfo] = useState(null);
  const [polyArea, setPolyArea] = useState(null);
  const [error, setError] = useState(false);
  const [tool, setTool] = useState("select");
  const [stroke, setStroke] = useState("#1E3A5F");
  const [fill, setFill] = useState("#C75B28");
  const [width, setWidth] = useState(2);
  const [bgOpacity, setBgOpacity] = useState(0.6);
  const [editVertices, setEditVertices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [arrowHead, setArrowHead] = useState(16);
  const [hist, setHist] = useState({ canUndo: false, canRedo: false });
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [rulerDialog, setRulerDialog] = useState(null);
  const [rulerValue, setRulerValue] = useState("");
  const [levels, setLevels] = useState([]);
  const [levelId, setLevelId] = useState("");
  const [zValue, setZValue] = useState("");
  const [polyCount, setPolyCount] = useState(0);
  const [bgEditMode, setBgEditMode] = useState(false);
  const [bgLocked, setBgLocked] = useState(true);
  const [scalePpm, setScalePpm] = useState(null);
  const [calibDialog, setCalibDialog] = useState(null);
  const [calibValue, setCalibValue] = useState("");
  const scalePpmRef = useRef(null);
  const calibRef = useRef(false);

  const syncHist = useCallback(() => {
    const h = historyRef.current;
    setHist({ canUndo: h.index > 0, canRedo: h.index < h.stack.length - 1 });
  }, []);

  // guardado automático (debounce tras unos segundos de inactividad)
  const scheduleAutosave = useCallback(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      if (dirtyRef.current && !savingRef.current && !bgEditModeRef.current) persistRef.current?.(true);
    }, 2500);
  }, []);

  const pushHistory = useCallback(() => {
    const fc = fcRef.current;
    const h = historyRef.current;
    if (!fc || h.locked) return;
    const json = JSON.stringify(fc.toJSON());
    if (h.stack[h.index] === json) return;
    h.stack = h.stack.slice(0, h.index + 1);
    h.stack.push(json);
    if (h.stack.length > 60) h.stack.shift();
    h.index = h.stack.length - 1;
    dirtyRef.current = true;
    setDirty(true);
    scheduleAutosave();
    syncHist();
  }, [syncHist, scheduleAutosave]);

  const applyHistory = useCallback((json) => {
    const fc = fcRef.current;
    if (!fc) return;
    historyRef.current.locked = true;
    fc.loadFromJSON(JSON.parse(json)).then(() => {
      fc.requestRenderAll();
      historyRef.current.locked = false;
      setToolAndMode(toolRef.current);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.index <= 0) return;
    h.index -= 1;
    applyHistory(h.stack[h.index]);
    dirtyRef.current = true; setDirty(true);
    scheduleAutosave();
    syncHist();
  }, [applyHistory, syncHist, scheduleAutosave]);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.index >= h.stack.length - 1) return;
    h.index += 1;
    applyHistory(h.stack[h.index]);
    dirtyRef.current = true; setDirty(true);
    scheduleAutosave();
    syncHist();
  }, [applyHistory, syncHist, scheduleAutosave]);

  const setToolAndMode = useCallback((t) => {
    setTool(t);
    toolRef.current = t;
    const fc = fcRef.current;
    if (!fc) return;
    fc.isDrawingMode = t === "freehand";
    if (t === "freehand") {
      const brush = new fabric.PencilBrush(fc);
      brush.color = styleRef.current.stroke;
      brush.width = styleRef.current.width;
      fc.freeDrawingBrush = brush;
    }
    fc.selection = t === "select";
    fc.forEachObject((o) => { o.selectable = t === "select"; o.evented = t === "select"; });
    fc.defaultCursor = t === "select" ? "default" : "crosshair";
    tempRef.current = null;
    fc.requestRenderAll();
  }, []);

  // 1) fetch drawing data
  useEffect(() => {
    let active = true;
    api.get(`/projects/${projectId}/designs/${designId}/drawings/${drawingId}`)
      .then((r) => {
        if (!active) return;
        setDrawing(r.data);
        if (r.data.background?.path) bgPathRef.current = r.data.background.path;
        if (r.data.background?.locked !== undefined && r.data.background?.locked !== null) setBgLocked(r.data.background.locked);
        if (r.data.level_id) setLevelId(r.data.level_id);
        if (r.data.z !== null && r.data.z !== undefined) setZValue(String(r.data.z));
        if (r.data.scale_ppm) { setScalePpm(r.data.scale_ppm); scalePpmRef.current = r.data.scale_ppm; }
      })
      .catch(() => setError(true));
    return () => { active = false; };
  }, [projectId, designId, drawingId]);

  // fetch model levels (for the floor/level selector)
  useEffect(() => {
    api.get("/model/levels").then((r) => setLevels(r.data || [])).catch(() => {});
  }, []);

  // fetch project info (para la cabecera de impresión)
  useEffect(() => {
    if (!projectId || projectId === "none") return;
    api.get(`/projects/${projectId}`).then((r) => setProjectInfo(r.data)).catch(() => {});
  }, [projectId]);

  // 2) init fabric once the <canvas> is actually mounted (drawing !== null)
  useEffect(() => {
    if (!drawing || !canvasElRef.current || fcRef.current) return undefined;
    const fc = new fabric.Canvas(canvasElRef.current, { backgroundColor: "#ffffff", preserveObjectStacking: true });
    fc.targetFindTolerance = 10;
    fcRef.current = fc;
    attachHandlers(fc);
    (async () => {
      if (drawing.data) { try { await fc.loadFromJSON(drawing.data); } catch { /* ignore */ } }
      fc.setDimensions({ width: W, height: H });
      if (drawing.background?.opacity != null) setBgOpacity(drawing.background.opacity);
      if (drawing.background?.path) {
        try { await applyBackground(fc, drawing.background.path, drawing.background.opacity ?? 0.6, drawing.background); } catch { /* ignore */ }
      }
      fc.requestRenderAll();
      setToolAndMode("select");
      historyRef.current = { stack: [JSON.stringify(fc.toJSON())], index: 0, locked: false };
      dirtyRef.current = false;
      setDirty(false);
      syncHist();
    })();
    return () => { if (fcRef.current) { fcRef.current.dispose(); fcRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing]);

  // keep style on selected object + brush
  useEffect(() => {
    styleRef.current = { stroke, fill: hexToRgba(fill, 0.15), width: Number(width), arrowHead: Number(arrowHead) };
    const fc = fcRef.current;
    if (!fc) return;
    if (fc.freeDrawingBrush) { fc.freeDrawingBrush.color = stroke; fc.freeDrawingBrush.width = Number(width); }
    const act = fc.getActiveObjects?.() || [];
    act.forEach((o) => {
      if (o.isArrow && o.getObjects) {
        o.getObjects().forEach((c) => {
          if (c.type === "triangle" || c.type === "Triangle") { c.set({ fill: stroke, stroke, width: Number(arrowHead), height: Number(arrowHead) }); }
          else { c.set({ stroke, strokeWidth: Number(width) }); }
        });
        o.set("dirty", true);
      } else if (o.type === "i-text") { o.set("fill", stroke); }
      else { o.set("stroke", stroke); if (o.type !== "line" && o.type !== "polyline" && o.type !== "path") o.set("fill", hexToRgba(fill, 0.15)); o.set("strokeWidth", Number(width)); }
    });
    if (act.length) { fc.requestRenderAll(); pushHistory(); }
  }, [stroke, fill, width, arrowHead, pushHistory]);

  // vertex editing toggle
  useEffect(() => {
    const fc = fcRef.current;
    if (!fc) return;
    const act = fc.getActiveObject?.();
    if (editVertices) {
      if (act && act.type === "polygon") enablePolygonEditing(act, fc);
      else toast.message("Selecciona un polígono para editar sus vértices.");
    } else {
      fc.getObjects("polygon").forEach((p) => disablePolygonEditing(p, fc));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editVertices]);

  // superficie del polígono seleccionado (m²) cuando la escala está calibrada
  const polyAreaM2 = (poly) => {
    const ppm = scalePpmRef.current;
    if (!poly || poly.type !== "polygon" || !ppm) return null;
    const pts = poly.points || [];
    if (pts.length < 3) return null;
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    }
    const areaPx = Math.abs(a / 2) * Math.abs((poly.scaleX || 1) * (poly.scaleY || 1));
    return areaPx / (ppm * ppm);
  };
  const updateSelectedArea = () => {
    const fc = fcRef.current; if (!fc) return;
    setPolyArea(polyAreaM2(fc.getActiveObject?.()));
  };

  const attachHandlers = (fc) => {
    fc.on("mouse:down", (opt) => {
      const t = toolRef.current;
      if (t === "select" || t === "freehand") return;
      const p = fc.getScenePoint(opt.e);
      const s = styleRef.current;
      if (t === "line") {
        const line = new fabric.Line([p.x, p.y, p.x, p.y], { stroke: s.stroke, strokeWidth: s.width, selectable: false });
        fc.add(line); tempRef.current = { obj: line, start: p };
      } else if (t === "arrow") {
        const line = new fabric.Line([p.x, p.y, p.x, p.y], { stroke: s.stroke, strokeWidth: s.width, selectable: false });
        fc.add(line); tempRef.current = { obj: line, start: p };
      } else if (t === "rect") {
        const rect = new fabric.Rect({ left: p.x, top: p.y, width: 1, height: 1, fill: s.fill, stroke: s.stroke, strokeWidth: s.width, selectable: false });
        fc.add(rect); tempRef.current = { obj: rect, start: p };
      } else if (t === "ellipse") {
        const el = new fabric.Ellipse({ left: p.x, top: p.y, rx: 1, ry: 1, fill: s.fill, stroke: s.stroke, strokeWidth: s.width, selectable: false, originX: "left", originY: "top" });
        fc.add(el); tempRef.current = { obj: el, start: p };
      } else if (t === "text") {
        const txt = new fabric.IText("Texto", { left: p.x, top: p.y, fill: s.stroke, fontSize: 24, fontFamily: "sans-serif" });
        fc.add(txt); setToolAndMode("select"); fc.setActiveObject(txt); txt.enterEditing(); txt.selectAll();
        pushHistory();
      } else if (t === "polygon") {
        handlePolygonClick(fc, p, s);
      } else if (t === "curve") {
        handleCurveClick(fc, p, s);
      } else if (t === "ruler") {
        handleRulerClick(fc, p, s);
      }
    });
    fc.on("mouse:move", (opt) => {
      const temp = tempRef.current;
      const t = toolRef.current;
      if (!temp || !temp.obj) return;
      const p = fc.getScenePoint(opt.e);
      if (t === "line") { temp.obj.set({ x2: p.x, y2: p.y }); }
      else if (t === "arrow") { temp.obj.set({ x2: p.x, y2: p.y }); }
      else if (t === "rect") {
        temp.obj.set({ left: Math.min(p.x, temp.start.x), top: Math.min(p.y, temp.start.y), width: Math.abs(p.x - temp.start.x), height: Math.abs(p.y - temp.start.y) });
      } else if (t === "ellipse") {
        temp.obj.set({ left: Math.min(p.x, temp.start.x), top: Math.min(p.y, temp.start.y), rx: Math.abs(p.x - temp.start.x) / 2, ry: Math.abs(p.y - temp.start.y) / 2 });
      }
      fc.requestRenderAll();
    });
    fc.on("mouse:up", () => {
      const t = toolRef.current;
      const temp = tempRef.current;
      if (t === "arrow" && temp?.obj) {
        const a = temp.start;
        const b = { x: temp.obj.x2, y: temp.obj.y2 };
        fc.remove(temp.obj);
        tempRef.current = null;
        if (Math.hypot(b.x - a.x, b.y - a.y) > 3) makeArrow(a, b);
        return;
      }
      if ((t === "line" || t === "rect" || t === "ellipse") && temp?.obj) {
        temp.obj.set({ selectable: true, evented: true });
        temp.obj.setCoords();
        tempRef.current = null;
        fc.requestRenderAll();
        pushHistory();
      }
    });
    fc.on("mouse:dblclick", () => {
      if (toolRef.current === "polygon") finishPolygon(fc);
    });
    fc.on("path:created", () => pushHistory());
    fc.on("object:modified", () => { pushHistory(); updateSelectedArea(); });
    fc.on("text:editing:exited", () => pushHistory());
    fc.on("selection:created", updateSelectedArea);
    fc.on("selection:updated", updateSelectedArea);
    fc.on("selection:cleared", () => setPolyArea(null));
  };

  // ---- arrow tool (flecha) ----
  const makeArrow = (a, b) => {
    const fc = fcRef.current; if (!fc) return;
    const s = styleRef.current;
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const headLen = s.arrowHead || Math.max(12, (s.width || 2) * 5);
    const shaft = new fabric.Line([a.x, a.y, b.x, b.y], { stroke: s.stroke, strokeWidth: s.width });
    const head = new fabric.Triangle({
      left: b.x, top: b.y, originX: "center", originY: "center",
      angle: (angle * 180) / Math.PI + 90,
      width: headLen, height: headLen, fill: s.stroke, stroke: s.stroke, strokeWidth: 0,
    });
    const group = new fabric.Group([shaft, head], {});
    group.isArrow = true;
    fc.add(group); group.setCoords();
    setToolAndMode("select"); fc.setActiveObject(group); fc.requestRenderAll();
    pushHistory();
  };

  // ---- polygon tool (click points) ----
  const polyRef = useRef(null);
  const handlePolygonClick = (fc, p, s) => {
    if (!polyRef.current) polyRef.current = { pts: [], preview: null };
    polyRef.current.pts.push({ x: p.x, y: p.y });
    setPolyCount(polyRef.current.pts.length);
    if (polyRef.current.preview) fc.remove(polyRef.current.preview);
    const pl = new fabric.Polyline(polyRef.current.pts, { stroke: s.stroke, strokeWidth: s.width, fill: "", selectable: false, evented: false });
    polyRef.current.preview = pl; fc.add(pl); fc.requestRenderAll();
  };
  const finishPolygon = (fc) => {
    const data = polyRef.current;
    if (!data || data.pts.length < 3) { if (data?.preview) fc.remove(data.preview); polyRef.current = null; setPolyCount(0); fc.requestRenderAll(); return; }
    if (data.preview) fc.remove(data.preview);
    const s = styleRef.current;
    const poly = new fabric.Polygon(data.pts, { stroke: s.stroke, strokeWidth: s.width, fill: s.fill });
    fc.add(poly); poly.setCoords();
    polyRef.current = null;
    setPolyCount(0);
    setToolAndMode("select");
    fc.setActiveObject(poly); fc.requestRenderAll();
    pushHistory();
    updateSelectedArea();
    toast.success("Polígono creado. Activa «Editar vértices» para deformarlo.");
  };

  // ---- curve tool (3 clicks: inicio, fin, control) ----
  const curveRef = useRef(null);
  const handleCurveClick = (fc, p, s) => {
    if (!curveRef.current) curveRef.current = [];
    curveRef.current.push(p);
    if (curveRef.current.length === 3) {
      const [a, b, c] = curveRef.current;
      const path = new fabric.Path(`M ${a.x} ${a.y} Q ${c.x} ${c.y} ${b.x} ${b.y}`, { stroke: s.stroke, strokeWidth: s.width, fill: "" });
      fc.add(path); path.setCoords();
      curveRef.current = null;
      setToolAndMode("select"); fc.setActiveObject(path); fc.requestRenderAll();
      pushHistory();
    } else {
      const dot = new fabric.Circle({ left: p.x - 2, top: p.y - 2, radius: 2, fill: s.stroke, selectable: false, evented: false });
      fc.add(dot); setTimeout(() => { try { fc.remove(dot); fc.requestRenderAll(); } catch {} }, 600);
    }
  };

  // ---- ruler tool (2 clics) + calibración de escala ----
  const rulerRef = useRef(null);
  const placeRuler = (a, b, text) => {
    const fc = fcRef.current; if (!fc) return;
    const s = styleRef.current;
    const sw = Math.max(1.5, (s.width || 2) * 0.8);
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const px = -(b.y - a.y) / len, py = (b.x - a.x) / len; // perpendicular unitario
    const tk = 7;
    const line = new fabric.Line([a.x, a.y, b.x, b.y], { stroke: "#C75B28", strokeWidth: sw });
    const tickA = new fabric.Line([a.x - px * tk, a.y - py * tk, a.x + px * tk, a.y + py * tk], { stroke: "#C75B28", strokeWidth: sw });
    const tickB = new fabric.Line([b.x - px * tk, b.y - py * tk, b.x + px * tk, b.y + py * tk], { stroke: "#C75B28", strokeWidth: sw });
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const label = new fabric.IText(text || "—", { left: mx, top: my - 11, fill: "#C75B28", fontSize: 13, fontFamily: "sans-serif", backgroundColor: "rgba(255,255,255,0.9)", originX: "center", originY: "center" });
    const group = new fabric.Group([line, tickA, tickB, label], {});
    fc.add(group); setToolAndMode("select"); fc.setActiveObject(group); fc.requestRenderAll();
    pushHistory();
  };
  const handleRulerClick = (fc, p) => {
    if (!rulerRef.current) { rulerRef.current = [p]; return; }
    const a = rulerRef.current[0];
    const b = p;
    rulerRef.current = null;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    if (calibRef.current) {
      setCalibValue("");
      setCalibDialog({ a, b, dist });
      return;
    }
    if (scalePpmRef.current) {
      const meters = dist / scalePpmRef.current;
      placeRuler(a, b, `${meters.toFixed(2).replace(".", ",")} m`);
    } else {
      setRulerValue("");
      setRulerDialog({ a, b });
    }
  };
  const confirmRuler = () => {
    const d = rulerDialog;
    if (!d) return;
    placeRuler(d.a, d.b, rulerValue.trim() || "—");
    setRulerDialog(null);
  };
  const startCalibration = () => {
    if (!bgPathRef.current) { toast.message("Primero sube una imagen de fondo."); return; }
    calibRef.current = true;
    rulerRef.current = null;
    setToolAndMode("ruler");
    toast.message("Marca dos puntos sobre una distancia conocida del plano.");
  };
  const confirmCalibration = () => {
    const d = calibDialog;
    if (!d) return;
    const meters = parseFloat(String(calibValue).replace(",", "."));
    if (!meters || meters <= 0) { toast.error("Indica una medida válida en metros."); return; }
    const ppm = d.dist / meters;
    scalePpmRef.current = ppm;
    setScalePpm(ppm);
    calibRef.current = false;
    placeRuler(d.a, d.b, `${meters.toFixed(2).replace(".", ",")} m`);
    setCalibDialog(null);
    updateSelectedArea();
    toast.success(`Escala calibrada: 1 m = ${ppm.toFixed(1)} px.`);
  };

  // delete + undo/redo keyboard
  useEffect(() => {
    const onKey = (e) => {
      const fc = fcRef.current;
      if (!fc) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      if (meta && (e.key === "y" || e.key === "Y")) { e.preventDefault(); redo(); return; }
      if (meta && (e.key === "a" || e.key === "A")) { e.preventDefault(); selectAll(); return; }
      if (e.key === "Enter" && toolRef.current === "polygon" && polyRef.current?.pts?.length >= 3) {
        e.preventDefault(); finishPolygon(fc); return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        const active = (fc.getActiveObjects?.() || []).filter((o) => !o.isBackground);
        if (active.length && !active.some((o) => o.isEditing)) {
          active.forEach((o) => fc.remove(o));
          fc.discardActiveObject(); fc.requestRenderAll();
          pushHistory();
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undo, redo, pushHistory]);

  const deleteSelected = () => {
    const fc = fcRef.current; if (!fc) return;
    (fc.getActiveObjects?.() || []).filter((o) => !o.isBackground).forEach((o) => fc.remove(o));
    fc.discardActiveObject(); fc.requestRenderAll();
    pushHistory();
  };

  const selectAll = () => {
    const fc = fcRef.current; if (!fc) return;
    if (toolRef.current !== "select") setToolAndMode("select");
    const objs = fc.getObjects().filter((o) => !o.isBackground);
    if (!objs.length) { toast.message("No hay nada dibujado todavía."); return; }
    fc.discardActiveObject();
    const sel = new fabric.ActiveSelection(objs, { canvas: fc });
    fc.setActiveObject(sel);
    fc.requestRenderAll();
  };

  const deleteAll = () => {
    const fc = fcRef.current; if (!fc) return;
    const objs = fc.getObjects().filter((o) => !o.isBackground);
    if (!objs.length) { toast.message("No hay nada dibujado todavía."); return; }
    if (!window.confirm(`¿Borrar los ${objs.length} elementos dibujados? La imagen de fondo se mantiene. Esta acción se puede deshacer con Ctrl+Z.`)) return;
    objs.forEach((o) => fc.remove(o));
    fc.discardActiveObject(); fc.requestRenderAll();
    pushHistory();
    toast.success("Se borró todo lo dibujado.");
  };

  const onUploadBg = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fc = fcRef.current; if (!fc) return;
    const form = new FormData();
    form.append("file", file);
    try {
      toast.message("Subiendo imagen de fondo…");
      const { data } = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      bgPathRef.current = data.path;
      await applyBackground(fc, data.path, bgOpacity);
      toast.success("Fondo añadido (vista cenital).");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir la imagen.");
    }
  };

  const applyBackground = async (fc, path, opacity, transform = null) => {
    const url = `${API}/files/${path}`;
    const img = await fabric.FabricImage.fromURL(url);
    if (transform && transform.scaleX) {
      img.set({
        originX: "center", originY: "center",
        left: transform.left ?? W / 2, top: transform.top ?? H / 2,
        scaleX: transform.scaleX, scaleY: transform.scaleY ?? transform.scaleX,
        opacity: Number(opacity),
      });
    } else {
      const scale = Math.min(W / img.width, H / img.height);
      img.set({ originX: "center", originY: "center", left: W / 2, top: H / 2, scaleX: scale, scaleY: scale, opacity: Number(opacity) });
    }
    img.isBackground = true;
    bgImgRef.current = img;
    fc.backgroundImage = img;
    fc.requestRenderAll();
    pushHistory();
  };

  const bgZoom = (factor) => {
    const fc = fcRef.current;
    const img = fc?.backgroundImage || bgImgRef.current;
    if (!fc || !img) { toast.message("Primero sube una imagen de fondo."); return; }
    const s = Math.min(8, Math.max(0.05, (img.scaleX || 1) * factor));
    img.set({ originX: "center", originY: "center", left: W / 2, top: H / 2, scaleX: s, scaleY: s });
    fc.requestRenderAll();
    pushHistory();
  };

  const bgFit = (mode) => {
    const fc = fcRef.current;
    const img = fc?.backgroundImage || bgImgRef.current;
    if (!fc || !img) { toast.message("Primero sube una imagen de fondo."); return; }
    const s = mode === "cover"
      ? Math.max(W / img.width, H / img.height)
      : Math.min(W / img.width, H / img.height);
    img.set({ originX: "center", originY: "center", left: W / 2, top: H / 2, scaleX: s, scaleY: s });
    fc.requestRenderAll();
    pushHistory();
  };

  // Recentra la imagen en el lienzo, la ajusta y desplaza la vista al centro
  const centerBackground = () => {
    const fc = fcRef.current;
    const img = fc?.backgroundImage || bgImgRef.current;
    if (!fc || !img) { toast.message("Primero sube una imagen de fondo."); return; }
    const s = Math.min(W / img.width, H / img.height);
    img.set({ originX: "center", originY: "center", left: W / 2, top: H / 2, scaleX: s, scaleY: s });
    fc.requestRenderAll();
    pushHistory();
    const area = canvasAreaRef.current;
    if (area) {
      area.scrollTo({ left: (area.scrollWidth - area.clientWidth) / 2, top: (area.scrollHeight - area.clientHeight) / 2, behavior: "smooth" });
    }
    toast.success("Imagen centrada en el lienzo.");
  };

  // Fija el fondo editado de vuelta como backgroundImage (sale del modo mover/escalar)
  const commitBgEdit = () => {
    const fc = fcRef.current; if (!fc) return;
    const img = bgImgRef.current;
    if (img) {
      try { fc.remove(img); } catch { /* noop */ }
      img.set({ selectable: false, evented: false });
      fc.backgroundImage = img;
    }
    fc.forEachObject((o) => { o.selectable = toolRef.current === "select"; o.evented = toolRef.current === "select"; });
    fc.requestRenderAll();
  };

  const toggleBgEdit = () => {
    const fc = fcRef.current; if (!fc) return;
    const img = fc.backgroundImage || bgImgRef.current;
    if (!bgEditMode) {
      if (bgLocked) { toast.message("El fondo está bloqueado. Desbloquéalo (candado) para moverlo."); return; }
      if (!img) { toast.message("Primero sube una imagen de fondo."); return; }
      setToolAndMode("select");
      fc.backgroundImage = null;
      img.isBackground = true;
      img.set({ selectable: true, evented: true, hasControls: true, hasBorders: true, lockRotation: true });
      img.setControlsVisibility?.({ mtr: false });
      fc.add(img);
      fc.sendObjectToBack(img);
      fc.forEachObject((o) => { if (o !== img) { o.selectable = false; o.evented = false; } });
      fc.setActiveObject(img);
      fc.requestRenderAll();
      setBgEditMode(true);
      toast.message("Arrastra el fondo para moverlo y usa las esquinas para escalarlo. Pulsa de nuevo para fijarlo.");
    } else {
      commitBgEdit();
      setBgEditMode(false);
      pushHistory();
      toast.success("Fondo fijado.");
    }
  };

  const toggleLock = () => {
    if (!bgLocked && bgEditMode) { commitBgEdit(); setBgEditMode(false); }
    setBgLocked((v) => !v);
  };

  useEffect(() => {
    const fc = fcRef.current;
    if (!fc) return;
    const img = fc.backgroundImage || bgImgRef.current;
    if (img) { img.set("opacity", Number(bgOpacity)); fc.requestRenderAll(); }
  }, [bgOpacity]);

  useEffect(() => { bgEditModeRef.current = bgEditMode; }, [bgEditMode]);
  useEffect(() => () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); }, []);

  // avisar si hay cambios sin guardar al cerrar/recargar la pestaña
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const goBack = () => {
    if (dirtyRef.current && !window.confirm("Tienes cambios sin guardar (se perderán los borrados y dibujos que no hayas guardado). ¿Salir sin guardar?")) return;
    navigate("/admin/disenos");
  };

  const persist = async (silent = false) => {
    const fc = fcRef.current; if (!fc) return;
    if (savingRef.current) return;
    if (!silent && bgEditMode) { commitBgEdit(); setBgEditMode(false); }
    savingRef.current = true;
    if (silent) setAutoSaving(true); else setSaving(true);
    try {
      const data = fc.toJSON();
      const bimg = bgImgRef.current;
      const background = bgPathRef.current ? {
        path: bgPathRef.current,
        opacity: Number(bgOpacity),
        left: bimg?.left, top: bimg?.top, scaleX: bimg?.scaleX, scaleY: bimg?.scaleY,
        locked: bgLocked,
      } : null;
      let thumbnail = null;
      try { thumbnail = fc.toDataURL({ format: "jpeg", quality: 0.5, multiplier: 280 / W }); } catch { thumbnail = null; }
      const z = zValue.trim() === "" ? null : Number(String(zValue).replace(",", "."));
      await api.put(`/projects/${projectId}/designs/${designId}/drawings/${drawingId}`, {
        data, background, thumbnail, level_id: levelId || "", z, scale_ppm: scalePpm,
      });
      dirtyRef.current = false;
      setDirty(false);
      if (!silent) toast.success("Dibujo guardado.");
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "No se pudo guardar.");
    } finally {
      savingRef.current = false;
      if (silent) setAutoSaving(false); else setSaving(false);
    }
  };
  persistRef.current = persist;
  const save = () => persist(false);

  const handlePrint = () => {
    const fc = fcRef.current; if (!fc) return;
    if (bgEditMode) { commitBgEdit(); setBgEditMode(false); }
    fc.discardActiveObject(); fc.requestRenderAll();
    let dataUrl = "";
    try { dataUrl = fc.toDataURL({ format: "png", multiplier: 2 }); } catch { toast.error("No se pudo generar la imagen."); return; }
    const w = window.open("", "_blank");
    if (!w) { toast.error("Permite las ventanas emergentes para imprimir."); return; }
    const projName = projectInfo?.name || (projectId === "none" ? "Diseño sin proyecto" : "Proyecto");
    const projLoc = projectInfo?.location || "";
    const faceTxt = FACE_LABEL[drawing.face] || drawing.face;
    const today = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
    const scaleTxt = scalePpm ? `Escala calibrada: 1&nbsp;m = ${scalePpm.toFixed(1)}&nbsp;px` : "";
    const origin = window.location.origin;
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${escapeHtml(drawing.name)} — ${escapeHtml(projName)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color:#1E3A5F; margin:0; }
  .hdr { display:flex; align-items:center; gap:14px; border-bottom:3px solid #C75B28; padding-bottom:10px; margin-bottom:16px; }
  .hdr img.logo { height:46px; width:46px; border-radius:8px; object-fit:cover; flex:0 0 auto; }
  .hdr .meta { flex:1 1 auto; min-width:0; }
  .hdr h1 { font-size:18px; margin:0; letter-spacing:-0.01em; }
  .hdr .loc { font-size:12px; color:#5b6b7a; margin-top:2px; }
  .hdr .draw { font-size:13px; font-weight:600; color:#C75B28; margin-top:4px; }
  .hdr .date { font-size:11px; color:#8a97a3; text-align:right; flex:0 0 auto; white-space:nowrap; }
  .canvas-wrap { text-align:center; }
  .canvas-wrap img { max-width:100%; max-height:165mm; border:1px solid #e2e6ea; border-radius:4px; }
  .foot { margin-top:12px; font-size:10px; color:#8a97a3; text-align:center; border-top:1px solid #eef1f4; padding-top:8px; }
</style></head>
<body>
  <div class="hdr">
    <img class="logo" src="${origin}/mori-logo.jpg" onerror="this.style.display='none'" />
    <div class="meta">
      <h1>${escapeHtml(projName)}</h1>
      <div class="loc">${escapeHtml(projLoc)}</div>
      <div class="draw">Dibujo: ${escapeHtml(drawing.name)} · ${escapeHtml(faceTxt)}</div>
    </div>
    <div class="date">${today}${scaleTxt ? "<br/>" + scaleTxt : ""}</div>
  </div>
  <div class="canvas-wrap"><img src="${dataUrl}" /></div>
  <div class="foot">Generado con Mori · Autopromoción</div>
  <script>window.onload=function(){setTimeout(function(){window.focus();window.print();},450);};<\/script>
</body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  if (error) return <NoAccess title="No se encontró el dibujo" backTo={`/admin/proyectos/${projectId}?tab=disenos`} />;
  if (!drawing) return <LoadingScreen text="Cargando el dibujo…" />;

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden" data-testid="drawing-editor">
      <header className="h-14 shrink-0 border-b border-border/70 bg-card flex items-center gap-3 px-3 sm:px-4">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={goBack} data-testid="editor-back">
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <div className="font-semibold truncate max-w-[22vw]">{drawing.name}</div>
        <span className="text-xs shrink-0" data-testid="autosave-indicator">
          {autoSaving || saving ? (
            <span className="text-muted-foreground">Guardando…</span>
          ) : dirty ? (
            <span className="text-[#C75B28] font-medium">• Sin guardar</span>
          ) : (
            <span className="text-muted-foreground hidden sm:inline">Guardado automático ✓</span>
          )}
        </span>
        <Badge variant="outline" className="gap-1 font-normal text-muted-foreground hidden lg:inline-flex"><MapPin className="h-3 w-3" /> {FACE_LABEL[drawing.face] || drawing.face}</Badge>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="icon" className="rounded-full h-9 w-9" onClick={undo} disabled={!hist.canUndo} title="Deshacer (Ctrl+Z)" data-testid="editor-undo"><Undo2 className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="rounded-full h-9 w-9" onClick={redo} disabled={!hist.canRedo} title="Rehacer (Ctrl+Mayús+Z)" data-testid="editor-redo"><Redo2 className="h-4 w-4" /></Button>
          <div className="h-5 w-px bg-border mx-0.5" />
          <Button variant="ghost" size="sm" className="rounded-full gap-1.5" onClick={selectAll} title="Seleccionar todo (Ctrl+A)" data-testid="editor-select-all"><CheckSquare className="h-4 w-4" /> <span className="hidden lg:inline">Sel. todo</span></Button>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={deleteSelected} title="Borrar selección (Supr)" data-testid="editor-delete"><Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">Borrar</span></Button>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-destructive hover:text-destructive" onClick={deleteAll} title="Borrar todo lo dibujado" data-testid="editor-delete-all"><Eraser className="h-4 w-4" /> <span className="hidden lg:inline">Borrar todo</span></Button>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={handlePrint} title="Imprimir / Guardar como PDF" data-testid="editor-print"><Printer className="h-4 w-4" /> <span className="hidden lg:inline">Imprimir</span></Button>
          <Button size="sm" className="rounded-full gap-1.5" onClick={save} disabled={saving} data-testid="editor-save"><Save className="h-4 w-4" /> {saving ? "Guardando…" : "Guardar"}</Button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Toolbar */}
        <aside className="w-16 shrink-0 border-r border-border/70 bg-card flex flex-col items-center gap-1 py-3 overflow-y-auto" data-testid="editor-toolbar">
          {TOOLS.map((t) => (
            <button
              key={t.key}
              onClick={() => setToolAndMode(t.key)}
              title={t.label}
              data-testid={`tool-${t.key}`}
              className={`h-11 w-11 rounded-xl flex items-center justify-center transition-colors ${tool === t.key ? "bg-[#C75B28] text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
            >
              <t.icon className="h-5 w-5" />
            </button>
          ))}
          <div className="h-px w-8 bg-border my-1" />
          <button
            onClick={() => setEditVertices((v) => !v)}
            title="Editar vértices (polígonos)"
            data-testid="tool-vertices"
            className={`h-11 w-11 rounded-xl flex items-center justify-center transition-colors ${editVertices ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
          >
            <Move3d className="h-5 w-5" />
          </button>
        </aside>

        {/* Canvas area */}
        <div ref={canvasAreaRef} className="relative flex-1 min-w-0 overflow-auto bg-[#eef1f4] p-6" data-testid="editor-canvas-area">
          <div className="mx-auto shadow-lg" style={{ width: W }}>
            <canvas ref={canvasElRef} data-testid="editor-canvas" />
          </div>
          {tool === "polygon" && (
            <div className="sticky bottom-4 left-0 right-0 flex justify-center pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-foreground/90 text-background px-4 py-2 text-sm shadow-lg backdrop-blur" data-testid="polygon-helper">
                <Waypoints className="h-4 w-4" />
                <span>{polyCount < 1 ? "Haz clic para añadir vértices" : `${polyCount} vértice${polyCount === 1 ? "" : "s"}`}</span>
                <Button size="sm" className="rounded-full h-7 px-3 ml-1" disabled={polyCount < 3} onClick={() => finishPolygon(fcRef.current)} data-testid="polygon-close-button">
                  Cerrar polígono
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Properties */}
        <aside className="w-56 shrink-0 border-l border-border/70 bg-card p-4 space-y-5 overflow-y-auto hidden md:block" data-testid="editor-properties">
          <div className="space-y-2">
            <Label className="text-xs">Color de trazo</Label>
            <input type="color" value={stroke} onChange={(e) => setStroke(e.target.value)} className="h-9 w-full rounded-md border border-border cursor-pointer" data-testid="prop-stroke" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Color de relleno</Label>
            <input type="color" value={fill} onChange={(e) => setFill(e.target.value)} className="h-9 w-full rounded-md border border-border cursor-pointer" data-testid="prop-fill" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Grosor de línea: {width}px</Label>
            <input type="range" min="1" max="12" value={width} onChange={(e) => setWidth(Number(e.target.value))} className="w-full" data-testid="prop-width" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5"><ArrowUpRight className="h-3.5 w-3.5" /> Punta de flecha: {arrowHead}px</Label>
            <input type="range" min="8" max="48" value={arrowHead} onChange={(e) => setArrowHead(Number(e.target.value))} className="w-full" data-testid="prop-arrow-head" />
            <p className="text-[11px] text-muted-foreground">Tamaño de la punta de las flechas (independiente del grosor de línea).</p>
          </div>
          <div className="pt-2 border-t border-border/60 space-y-2">
            <Label className="text-xs flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" /> Imagen de fondo</Label>
            <label className="block">
              <input type="file" accept="image/*" onChange={onUploadBg} className="hidden" data-testid="prop-bg-input" />
              <span className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 cursor-pointer hover:bg-secondary">
                <ImageIcon className="h-4 w-4" /> Subir plano / mapa
              </span>
            </label>
            <Button variant="default" size="sm" className="w-full rounded-full gap-1.5 mt-1" onClick={centerBackground} data-testid="bg-center">
              <Crosshair className="h-4 w-4" /> Centrar imagen en pantalla
            </Button>
            <div className="grid grid-cols-[1fr_auto] gap-1.5">
              <Button variant={bgEditMode ? "default" : "outline"} size="sm" className="rounded-full gap-1.5" onClick={toggleBgEdit} disabled={bgLocked && !bgEditMode} data-testid="bg-edit-toggle">
                <Move className="h-4 w-4" /> {bgEditMode ? "Fijar fondo" : "Mover / escalar"}
              </Button>
              <Button variant={bgLocked ? "default" : "outline"} size="icon" className="rounded-full h-9 w-9" onClick={toggleLock} title={bgLocked ? "Fondo bloqueado (clic para desbloquear)" : "Fondo desbloqueado (clic para bloquear)"} data-testid="bg-lock-toggle">
                {bgLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
              </Button>
            </div>
            <Label className="text-xs">Opacidad del fondo: {Math.round(bgOpacity * 100)}%</Label>
            <input type="range" min="0" max="1" step="0.05" value={bgOpacity} onChange={(e) => setBgOpacity(Number(e.target.value))} className="w-full" data-testid="prop-bg-opacity" />
            <div className="grid grid-cols-2 gap-1.5 pt-1">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => bgZoom(1.15)} data-testid="bg-zoom-in">Agrandar +</Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => bgZoom(1 / 1.15)} data-testid="bg-zoom-out">Reducir −</Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => bgFit("contain")} data-testid="bg-fit-contain">Ajustar</Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => bgFit("cover")} data-testid="bg-fit-cover">Rellenar</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Si no ves la imagen, pulsa «Centrar imagen en pantalla». El candado evita moverla sin querer.</p>
          </div>

          <div className="pt-2 border-t border-border/60 space-y-2">
            <Label className="text-xs flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5" /> Escala real</Label>
            <Button variant="outline" size="sm" className="w-full rounded-full gap-1.5" onClick={startCalibration} data-testid="calibrate-scale">
              <RulerIcon className="h-4 w-4" /> Calibrar con una medida
            </Button>
            <p className="text-[11px] text-muted-foreground" data-testid="scale-status">
              {scalePpm ? `Calibrado: 1 m = ${scalePpm.toFixed(1)} px. La regla medirá en metros automáticamente.` : "Sin calibrar. Pulsa «Calibrar» y marca una distancia conocida del plano."}
            </p>
            {polyArea !== null && (
              <div className="rounded-lg bg-[#C75B28]/10 border border-[#C75B28]/30 px-3 py-2 mt-1" data-testid="poly-area">
                <div className="text-[11px] text-muted-foreground leading-tight">Superficie del polígono seleccionado</div>
                <div className="text-base font-semibold text-[#C75B28] leading-tight mt-0.5">{polyArea.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²</div>
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-border/60 space-y-2">
            <Label className="text-xs flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" /> Ubicación del suelo (XYZ)</Label>
            <Select value={levelId || "none"} onValueChange={(v) => setLevelId(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="prop-level-select"><SelectValue placeholder="Nivel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="prop-level-option-none">Sin nivel</SelectItem>
                {levels.map((l) => (
                  <SelectItem key={l.level_id} value={l.level_id} data-testid={`prop-level-option-${l.level_id}`}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label className="text-xs">Cota Z (m) respecto a Z=0</Label>
            <Input type="text" inputMode="decimal" placeholder="Ej. -2,00" value={zValue} onChange={(e) => setZValue(e.target.value)} className="h-9" data-testid="prop-z-input" />
            <p className="text-[11px] text-muted-foreground">Z negativa = por debajo del punto 0 (ej. −2 m bajo el nivel 0).</p>
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed pt-2 border-t border-border/60">
            Polígono: haz clic para añadir cada vértice (los lados que quieras) y pulsa «Cerrar polígono» o Enter (o doble clic) para cerrar la figura. Luego activa «Editar vértices» y arrastra cualquier vértice para agrandar o encoger la figura.
          </p>
        </aside>
      </div>

      <Dialog open={Boolean(rulerDialog)} onOpenChange={(v) => { if (!v) setRulerDialog(null); }}>
        <DialogContent data-testid="ruler-dialog">
          <DialogHeader>
            <DialogTitle>Dimensión de la regla</DialogTitle>
            <DialogDescription>Indica la medida real del tramo que acabas de marcar (p. ej. «5 m», «3,20 m», «450 cm»).</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Label htmlFor="ruler-dim" className="text-xs">Dimensión</Label>
            <Input
              id="ruler-dim"
              autoFocus
              data-testid="ruler-dimension-input"
              placeholder="Ej. 5 m"
              value={rulerValue}
              onChange={(e) => setRulerValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmRuler(); }}
              className="mt-1.5"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setRulerDialog(null)} data-testid="ruler-cancel">Cancelar</Button>
            <Button className="rounded-full" onClick={confirmRuler} data-testid="ruler-confirm">Colocar regla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(calibDialog)} onOpenChange={(v) => { if (!v) { setCalibDialog(null); calibRef.current = false; } }}>
        <DialogContent data-testid="calibration-dialog">
          <DialogHeader>
            <DialogTitle>Calibrar escala</DialogTitle>
            <DialogDescription>¿Cuántos metros mide en la realidad el tramo que acabas de marcar? A partir de ahí, la regla medirá todo en metros.</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Label htmlFor="calib-dim" className="text-xs">Longitud real (metros)</Label>
            <Input
              id="calib-dim"
              autoFocus
              data-testid="calibration-input"
              inputMode="decimal"
              placeholder="Ej. 5"
              value={calibValue}
              onChange={(e) => setCalibValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmCalibration(); }}
              className="mt-1.5"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setCalibDialog(null); calibRef.current = false; }} data-testid="calibration-cancel">Cancelar</Button>
            <Button className="rounded-full" onClick={confirmCalibration} data-testid="calibration-confirm">Calibrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function hexToRgba(hex, alpha) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function escapeHtml(str) {
  return String(str ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
